import { useLocation } from '@remix-run/react'
import clsx from 'clsx'
import {
  LucideChevronFirst,
  LucideChevronLeft,
  LucideChevronRight,
  LucideChevronsRight,
} from 'lucide-react'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  aCardsCollapse,
  aHoverFeature,
  aSelectedListing,
  type HoverFeature,
  useAtom,
  useAtomValue,
  useSetAtom,
} from '~/atoms'
import { ClientOnly, ControlTooltip } from '~/components/common'
import { Button } from '~/components/ui'
import { useBreakpoint } from '~/hooks'
import { type MapListingProjection } from '~/models'
import { cn, debounce, fmtAddress } from '~/utils'
import MapListingCard from './Card'
import MapListingCardSkeleton from './CardSkeleton'

const INITIAL_RENDERED_MAP_CARDS = 8
const DEFERRED_MAP_CARD_HEIGHT = 430

function getListingHoverFeature(listing: MapListingProjection): HoverFeature {
  return {
    address: listing.isBusinessListing
      ? fmtAddress(listing.property, {
          locationVisibility: listing.locationVisibility,
        })
      : listing.property.address,
    isBusinessListing:
      (listing.isBusinessListing as unknown as boolean) || false,
    lat: listing.property.latitude,
    lng: listing.property.longitude,
    name: listing.name,
    propertyTypes: listing.propertyTypes.join(', '),
    status: listing.status,
    type: listing.type,
    _id: listing._id,
    thumbUrl:
      listing.images && listing.images.length > 0
        ? listing.images[0].thumbUrl
        : undefined,
  }
}

function DeferredMapListingCard({
  className,
  listing,
  renderImmediately,
  selectedListing,
  setHoverFeature,
  showOriginalAttachments,
  showOriginalBrokers,
}: {
  className?: string
  listing: MapListingProjection
  renderImmediately: boolean
  selectedListing: { _id: string } | null
  setHoverFeature: (feature: HoverFeature | null) => void
  showOriginalAttachments: boolean
  showOriginalBrokers: boolean
}) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [shouldRender, setShouldRender] = useState(renderImmediately)

  useEffect(() => {
    if (renderImmediately) {
      setShouldRender(true)
    }
  }, [renderImmediately])

  useEffect(() => {
    if (shouldRender) return

    const node = containerRef.current
    if (!node) return

    if (!window.IntersectionObserver) {
      setShouldRender(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldRender(true)
          observer.disconnect()
        }
      },
      {
        root: document.getElementById('cards-list'),
        rootMargin: '800px 0px',
      }
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [shouldRender])

  return (
    <div
      ref={containerRef}
      className={className}
      style={shouldRender ? undefined : { minHeight: DEFERRED_MAP_CARD_HEIGHT }}
      onMouseEnter={() => {
        if (!selectedListing) {
          setHoverFeature(getListingHoverFeature(listing))
        }
      }}
      onMouseLeave={() => {
        setHoverFeature(null)
      }}>
      {shouldRender && (
        <MapListingCard
          className="w-full"
          showOriginalAttachments={showOriginalAttachments}
          showOriginalBrokers={showOriginalBrokers}
          listing={listing}
        />
      )}
    </div>
  )
}

function MapCardList({
  className,
  listings,
  isLoading,
  isFetching,
  fetchNextPage,
  hasNextPage,
  showOriginalAttachments = false,
  showOriginalBrokers = false,
}: {
  className?: string
  listings: MapListingProjection[]
  reportSelectedListing?: MapListingProjection | null
  isLoading?: boolean
  isFetching?: boolean
  fetchNextPage?: () => void
  hasNextPage?: boolean
  showOriginalAttachments?: boolean
  showOriginalBrokers?: boolean
}) {
  const location = useLocation()
  const setHoverFeature = useSetAtom(aHoverFeature)
  const selectedListing = useAtomValue(aSelectedListing)
  const [cardCollapse, setCardCollapse] = useAtom(aCardsCollapse)
  const { xlDown } = useBreakpoint()

  useEffect(() => {
    const elem = document.getElementById('cards-list')
    if (!fetchNextPage) {
      return
    }
    const onScroll = () => {
      if (
        hasNextPage &&
        !isFetching &&
        elem!.scrollTop > elem!.scrollHeight - elem!.scrollHeight / 4
      ) {
        fetchNextPage()
      }
    }
    const debouncedOnScroll = debounce(onScroll, 100)
    elem!.addEventListener('scroll', debouncedOnScroll)
    return () => elem!.removeEventListener('scroll', debouncedOnScroll)
  }, [hasNextPage, isFetching, fetchNextPage])

  const isReportPage = useMemo(() => {
    return location.pathname.includes('/report')
  }, [location])

  const onCollapseClick = useCallback(() => {
    if (cardCollapse === 'double') {
      if (xlDown) {
        setCardCollapse('none')
      } else {
        setCardCollapse('single')
      }
    } else if (cardCollapse === 'single') {
      setCardCollapse('none')
    } else if (!xlDown) {
      setCardCollapse('double')
    } else {
      setCardCollapse('single')
    }
  }, [cardCollapse, setCardCollapse, xlDown])

  const collapseText = useMemo(() => {
    if (cardCollapse === 'none') {
      return 'Show Cards'
    } else if (cardCollapse === 'single') {
      return 'Hide Cards'
    }
    return 'Show Fewer Cards'
  }, [cardCollapse, xlDown])

  const collapseIcon = useMemo(() => {
    if (cardCollapse === 'none') {
      if (xlDown) {
        return <LucideChevronRight className="h-6 w-6" />
      }
      return <LucideChevronsRight className="h-6 w-6" />
    } else if (
      cardCollapse === 'single' ||
      (cardCollapse === 'double' && xlDown)
    ) {
      return <LucideChevronFirst className="h-6 w-6" />
    }
    return <LucideChevronLeft className="h-6 w-6" />
  }, [cardCollapse, xlDown])

  return (
    <div className={cn(className, 'relative hidden lg:flex')}>
      <div
        id="cards-list"
        className={cn(
          'z-40 grid w-full place-content-start gap-4 overflow-y-auto overflow-x-hidden pb-4',
          isReportPage
            ? 'lg:grid-cols-1 2xl:grid-cols-2'
            : {
                'lg:grid-cols-1 2xl:grid-cols-2':
                  cardCollapse === 'double' &&
                  (listings.length > 1 || isLoading),
                'lg:grid-cols-1':
                  cardCollapse === 'single' ||
                  (listings.length <= 1 && !isLoading),
                hidden: cardCollapse === 'none',
              }
        )}>
        {isLoading && listings.length === 0
          ? Array.from({ length: 8 }, (_, index) => index).map(
              (index) => {
                return (
                  <MapListingCardSkeleton
                    key={index}
                    className={clsx('md:w-[350px] 2xl:w-[350px]')}
                  />
                )
              }
            )
          : listings.map((listing, index) => {
              return (
                <DeferredMapListingCard
                  key={listing._id}
                  className={clsx(
                    isFetching && 'animate-pulse',
                    'md:w-[350px] 2xl:w-[350px]'
                  )}
                  listing={listing}
                  renderImmediately={index < INITIAL_RENDERED_MAP_CARDS}
                  selectedListing={selectedListing}
                  setHoverFeature={setHoverFeature}
                  showOriginalAttachments={showOriginalAttachments}
                  showOriginalBrokers={showOriginalBrokers}
                />
              )
            })}
        {hasNextPage && fetchNextPage && (
          <div className="col-span-full flex justify-center">
            <Button disabled={isFetching} onClick={() => fetchNextPage()}>
              {isFetching ? 'Loading' : 'Load More'}
            </Button>
          </div>
        )}
      </div>

      {!isReportPage && (
        <ClientOnly>
          <ControlTooltip side="right" text={collapseText}>
            <Button
              variant="map"
              size="map"
              className="fixed left-4 top-4 z-50"
              onClick={onCollapseClick}>
              <span className="sr-only">Collapse Cards</span>
              {collapseIcon}
            </Button>
          </ControlTooltip>
        </ClientOnly>
      )}
    </div>
  )
}

export default memo(MapCardList)
